import { createContext, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { DICTIONARY_TABLE_COLUMNS, ENTITY_CONFIG_TYPE, FLEXIBLE } from '@constants'

import { TableWithFilters } from './components/TableWithFilters'
import { useConfigDictionary } from './hooks'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const Dictionary: FC = () => {
  const { t } = useTranslation()

  const { state, data, handlers } = useConfigDictionary()

  const { categoryId } = state

  const { dictionariesData, dictionariesIsLoading, dictionaryCategories, countPage } = data

  const { handleSelectCategory, handleResetCategory, handleLoadMore } = handlers

  const contextValue = {
    allDictionaries: dictionariesData,
    dictionaryCategories,
    currentCategory: categoryId,
    selectCategory: handleSelectCategory,
    resetCategory: handleResetCategory,
    dictionariesIsLoading,
    countPage: countPage ?? 1,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.DICTIONARIES}>
        <EditConfigScriptDialog />
        <Box sx={FLEXIBLE}>
          <PageTitle
            end={
              <Box sx={{ py: 0.5 }}>
                <EditConfigMenu />
              </Box>
            }
            start={
              <>
                <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
                  {t('pageTitle.models')}
                </Typography>
                <Typography display={'inline'} variant={'subtitle2'}>
                  {t('pageTitle.dictionary')}
                </Typography>
              </>
            }
          />
          <PageContentLayout>
            <ExportAllCheckbox />
            <TableWithFilters
              columns={DICTIONARY_TABLE_COLUMNS}
              countPage={countPage ?? 1}
              externalOnSelectChange={handleSelectCategory}
              filterName='Categories'
              filterOptions={dictionaryCategories || []}
              handleLoadMore={handleLoadMore}
              loading={dictionariesIsLoading}
              placeholderInput={t('dictionary.searchPlaceholder')}
              rows={dictionariesData ?? []}
              onRefresh={handleResetCategory}
            />
          </PageContentLayout>
          <ExportActionsPanel />
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
